import type { QueryClient, QueryKey } from '@tanstack/vue-query';
import { withAcceptedSameIdUpdate } from '@/modules/viewer/composables/use-viewer-selection.ts';

const CHANNEL_NAME = 'alphadoc-entity-updates';
const STORAGE_KEY = 'alphadoc:entity-update';

export interface EntityUpdateEvent {
  type: 'entity-updated';
  entityType: string;
  entityId: number;
  eventId: string;
}

export interface ActiveEntityContext {
  scope: string | null;
  rowId: string | null;
  refresh: () => Promise<unknown> | unknown;
}

export interface EntityUpdateSyncOptions {
  entityType: string;
  matchesQuery: (queryKey: QueryKey, entityId: number) => boolean;
  getActiveEntityContext?: (entityId: number) => ActiveEntityContext | null;
}

let channel: BroadcastChannel | null = null;
let channelSubscriptions = 0;

function getChannel() {
  if (typeof BroadcastChannel !== 'function') {
    return null;
  }

  try {
    channel ??= new BroadcastChannel(CHANNEL_NAME);
    return channel;
  } catch {
    return null;
  }
}

function isEntityUpdateEvent(value: unknown): value is EntityUpdateEvent {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const event = value as Partial<EntityUpdateEvent>;

  return event.type === 'entity-updated' && typeof event.entityType === 'string' && typeof event.entityId === 'number';
}

function subscribe(listener: (event: EntityUpdateEvent) => void) {
  const broadcastChannel = getChannel();

  if (broadcastChannel) {
    const onMessage = ({ data }: MessageEvent) => {
      if (isEntityUpdateEvent(data)) {
        listener(data);
      }
    };
    broadcastChannel.addEventListener('message', onMessage);
    channelSubscriptions += 1;

    return () => {
      broadcastChannel.removeEventListener('message', onMessage);
      channelSubscriptions -= 1;
      if (channelSubscriptions === 0) {
        broadcastChannel.close();
        if (channel === broadcastChannel) {
          channel = null;
        }
      }
    };
  }

  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const onStorage = ({ key, newValue }: StorageEvent) => {
    if (key !== STORAGE_KEY || !newValue) {
      return;
    }

    try {
      const event: unknown = JSON.parse(newValue);
      if (isEntityUpdateEvent(event)) {
        listener(event);
      }
    } catch {
      // Ignore malformed values written by older clients or browser extensions.
    }
  };

  window.addEventListener('storage', onStorage);

  return () => window.removeEventListener('storage', onStorage);
}

export function publishEntityUpdated(entityType: string, entityId: number) {
  const event: EntityUpdateEvent = {
    type: 'entity-updated',
    entityType,
    entityId,
    eventId: `${Date.now()}-${Math.random()}`,
  };

  const broadcastChannel = getChannel();

  if (broadcastChannel) {
    broadcastChannel.postMessage(event);
    return;
  }

  try {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(event));
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Cross-tab sync is an enhancement; saving the entity must still succeed if storage is unavailable.
  }
}

export function setupEntityUpdateSync(queryClient: QueryClient, options: EntityUpdateSyncOptions) {
  return subscribe(({ entityType, entityId }) => {
    if (entityType !== options.entityType) {
      return;
    }

    const context = options.getActiveEntityContext?.(entityId) ?? null;
    const accepted = context?.scope && context.rowId ? { scope: context.scope, rowId: context.rowId } : null;
    const refresh = () => {
      const tasks: Promise<unknown>[] = [
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) => options.matchesQuery(queryKey, entityId),
        }),
      ];

      if (context) {
        tasks.push(Promise.resolve().then(context.refresh));
      }

      return Promise.allSettled(tasks);
    };

    if (accepted) {
      void withAcceptedSameIdUpdate(accepted.scope, accepted.rowId, refresh);
    } else {
      void refresh();
    }
  });
}
